/**
 * WhatsNew — the plain-language "What's new in this update" summary that
 * slots into the adult variant of ``ReacceptTermsGate``.
 *
 * Reads a single per-release entry (typically
 * ``terms_release_notes.json[String(currentTermsVersion)]``) and renders
 * a heading + effective-date pill + optional summary paragraph + 1-5
 * bullets. Each bullet gets a calm sage check medallion — never danger
 * red.
 *
 * The section is ALWAYS visible — a minor release shows one bullet, a
 * missing entry falls back to a single generic "small wording updates"
 * line. An empty/absent section reads as alarming, which is the
 * opposite of what we want for a Terms update.
 *
 * Design reference: Drive handoff "Reaccept Terms · Update Summary"
 * (rc_reaccept_with_summary.jsx, 2026-06-22). No new tokens introduced.
 */

import { Icon } from "../chrome/Icon";

export interface TermsReleaseEntry {
  /** ISO YYYY-MM-DD; rendered "June 15, 2026" without timezone drift. */
  effective?: string;
  /** Section title; defaults to "What's new in this update". */
  heading?: string;
  /** One short intro paragraph; ``null``/absent on minor releases. */
  summary?: string | null;
  /** 1-5 plain-language bullets. The UI renders the first 5 and ignores the rest. */
  bullets?: string[];
  /** Hint for typo/contact-only releases — doesn't change layout. */
  minor?: boolean;
}

/** Last-resort entry when a version ships without a notes file row. The
 *  blocker still appears, but the section never reads as empty. */
const FALLBACK_RELEASE: TermsReleaseEntry = {
  heading: "What changed in this update",
  summary: null,
  bullets: ["Small wording updates — see the full Terms for details."],
  minor: true,
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Parse YYYY-MM-DD without instantiating a timezone-shifted Date.  */
function formatEffective(iso: string | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return iso;
  return `${month} ${Number(m[3])}, ${m[1]}`;
}

interface Props {
  /** The matching ``terms_release_notes.json`` entry. ``undefined`` is fine
   *  — the component falls back to a calm one-bullet line. */
  release?: TermsReleaseEntry;
}

export function WhatsNew({ release }: Props): JSX.Element {
  const safe =
    release && Array.isArray(release.bullets) && release.bullets.length > 0
      ? release
      : FALLBACK_RELEASE;
  const bullets = (safe.bullets ?? []).slice(0, 5);
  const effective = formatEffective(safe.effective);

  return (
    <section
      aria-label="Summary of changes"
      style={{
        textAlign: "left",
        background:
          "color-mix(in srgb, var(--color-sky, #c2d5db) 22%, var(--color-base, #f5e8c8))",
        border:
          "1px solid color-mix(in srgb, var(--color-ink, #2f3a45) 8%, transparent)",
        borderRadius: 16,
        padding: "16px 17px 17px",
        margin: "4px 0 18px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: safe.summary ? 8 : 10,
        }}
      >
        <h3
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 16,
            fontWeight: 800,
            margin: 0,
            color: "#2f3a45",
            fontFamily: "inherit",
          }}
        >
          <Icon name="sparkles" size={17} stroke="#98664d" />
          {safe.heading || "What's new in this update"}
        </h3>
        {effective && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 9px",
              borderRadius: 999,
              whiteSpace: "nowrap",
              background:
                "color-mix(in srgb, #2f3a45 6%, #f5e8c8)",
              color: "#6b7580",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <Icon name="calendar" size={13} stroke="#6b7580" />
            In effect {effective}
          </span>
        )}
      </div>

      {safe.summary && (
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "#2f3a45",
            margin: "0 0 12px",
          }}
        >
          {safe.summary}
        </p>
      )}

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: 9,
        }}
      >
        {bullets.map((b, i) => (
          <li
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: 14,
              lineHeight: 1.45,
              color: "#2f3a45",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                flexShrink: 0,
                marginTop: 1,
                display: "grid",
                placeItems: "center",
                background:
                  "color-mix(in srgb, #a8bf91 38%, #f5e8c8)",
                color: "#3a6e3f",
              }}
            >
              <Icon name="check" size={13} stroke="#3a6e3f" strokeWidth={2.25} />
            </span>
            <span style={{ flex: 1 }}>{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default WhatsNew;
